import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { QuestionContextValue, QuestionHandlers, QuestionType } from "./types";
import { ANATOMY_ACTIONS } from "./anatomyReducer";

const LEGACY_ACTIONS = {
  CSCQ_SELECT_OPTION: "CSCQ/Select Option",
  CSCQ_SUBMIT: "CSCQ/Submit",
  CSCQ_RESET: "CSCQ/Reset",
  MSCQ_SELECT_OPTION: "MSCQ/Select Option",
  MSCQ_SUBMIT: "MSCQ/Submit",
  MSCQ_RESET: "MSCQ/Reset",
  UPDATE_OEQ: "UPDATE_OEQ",
  SUBMIT_OEQ: "SUBMIT_OEQ",
  RESET_OEQ: "RESET_OEQ",
};

interface CSCQState {
  selected?: number;
  disabled?: number[];
}

interface MSCQState {
  selected?: number[];
  disabled?: number[];
  attemptsCount?: number;
  hasAttempted?: boolean;
}

interface OEQState {
  text?: string;
  textChanged?: boolean;
  complete?: boolean;
  resubmitted?: boolean;
}

interface AnatomyReduxState {
  answers: Record<string, string>;
  correctAnswers: string[];
  incorrectAnswers: string[];
  isSubmitted: boolean;
  showFeedback: boolean;
}

interface LegacyState {
  cscq?: Record<string, CSCQState>;
  mscq?: Record<string, MSCQState>;
  oeq?: Record<string, OEQState>;
  anatomy?: Record<string, AnatomyReduxState>;
}

const QuestionContext = React.createContext<QuestionContextValue | null>(null);

interface QuestionProviderProps {
  children: React.ReactNode;
}

export const QuestionProvider: React.FC<QuestionProviderProps> = ({
  children,
}) => {
  const dispatch = useDispatch();

  const cscqState = useSelector((state: LegacyState) => state.cscq || {});
  const mscqState = useSelector((state: LegacyState) => state.mscq || {});
  const oeqState = useSelector((state: LegacyState) => state.oeq || {});
  const anatomyState = useSelector((state: LegacyState) => state.anatomy || {});

  const getSingleSelectState = React.useCallback(
    (componentId: string | number) => {
      const id = String(componentId);
      const state = cscqState[id] || {};
      return {
        selectedOptionIndex: state.selected,
        submittedOptionIndexes: state.disabled || [],
      };
    },
    [cscqState]
  );

  const getMultiSelectState = React.useCallback(
    (componentId: string | number) => {
      const id = String(componentId);
      const state = mscqState[id] || {};
      return {
        selectedOptionIds: state.selected || [],
        submittedOptionIds: state.disabled || [],
        attemptsCount: state.attemptsCount || 0,
        hasAttempted: state.hasAttempted || false,
      };
    },
    [mscqState]
  );

  const getOpenEndedState = React.useCallback(
    (componentId: string | number) => {
      const id = String(componentId);
      const state = oeqState[id] || {};
      return {
        value: state.text || "",
        isCompleted: state.complete || false,
        textChanged: state.textChanged || false,
      };
    },
    [oeqState]
  );

  const getAnatomyState = React.useCallback(
    (componentId: string | number) => {
      const id = String(componentId);
      const state = anatomyState[id] || {};
      return {
        answers: state.answers || {},
        correctAnswers: state.correctAnswers || [],
        incorrectAnswers: state.incorrectAnswers || [],
        isSubmitted: state.isSubmitted || false,
        showFeedback: state.showFeedback || false,
      };
    },
    [anatomyState]
  );

  const getQuestionHandlers = React.useCallback(
    (componentId: string | number, type: QuestionType): QuestionHandlers => {
      const id = String(componentId);

      return {
        selectOption: (optionIndex: number) => {
          dispatch({
            type: LEGACY_ACTIONS.CSCQ_SELECT_OPTION,
            payload: { componentID: id, optionID: optionIndex },
          });
        },

        toggleOption: (optionId: number) => {
          dispatch({
            type: LEGACY_ACTIONS.MSCQ_SELECT_OPTION,
            payload: { componentID: id, optionID: optionId },
          });
        },

        setValue: (value: string) => {
          dispatch({
            type: LEGACY_ACTIONS.UPDATE_OEQ,
            payload: { componentID: id, text: value },
          });
        },

        setAnswer: (calloutId: string, value: string) => {
          dispatch({
            type: ANATOMY_ACTIONS.SET_ANSWER,
            payload: { componentID: id, calloutId, value },
          });
        },

        submitAnatomy: (callouts: { id: string; correctAnswer: string }[]) => {
          dispatch({
            type: ANATOMY_ACTIONS.SUBMIT,
            payload: { componentID: id, callouts },
          });
        },

        submit: () => {
          switch (type) {
            case QuestionType.SingleSelect:
              dispatch({ type: LEGACY_ACTIONS.CSCQ_SUBMIT, payload: id });
              break;
            case QuestionType.MultiSelect:
              dispatch({ type: LEGACY_ACTIONS.MSCQ_SUBMIT, payload: id });
              break;
            case QuestionType.OpenEnded:
              dispatch({ type: LEGACY_ACTIONS.SUBMIT_OEQ, payload: id });
              break;
          }
        },

        reset: () => {
          switch (type) {
            case QuestionType.SingleSelect:
              dispatch({ type: LEGACY_ACTIONS.CSCQ_RESET, payload: id });
              break;
            case QuestionType.MultiSelect:
              dispatch({ type: LEGACY_ACTIONS.MSCQ_RESET, payload: id });
              break;
            case QuestionType.OpenEnded:
              dispatch({ type: LEGACY_ACTIONS.RESET_OEQ, payload: id });
              break;
            case QuestionType.Anatomy:
              dispatch({
                type: ANATOMY_ACTIONS.RESET,
                payload: id,
              });
              break;
          }
        },

        tryAgain: () => {
          if (type === QuestionType.MultiSelect) {
            dispatch({ type: LEGACY_ACTIONS.MSCQ_RESET, payload: id });
          }
        },
      };
    },
    [dispatch]
  );

  const contextValue = React.useMemo<QuestionContextValue>(
    () => ({
      getSingleSelectState,
      getMultiSelectState,
      getOpenEndedState,
      getAnatomyState,
      getQuestionHandlers,
    }),
    [
      getSingleSelectState,
      getMultiSelectState,
      getOpenEndedState,
      getAnatomyState,
      getQuestionHandlers,
    ]
  );

  return (
    <QuestionContext.Provider value={contextValue}>
      {children}
    </QuestionContext.Provider>
  );
};

export const useQuestionContext = (): QuestionContextValue | null => {
  return React.useContext(QuestionContext);
};
